import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import {
  ContactsDetailsPageRouteQuery,
  DeleteFinanceContactDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'

type FinanceContact = ContactsDetailsPageRouteQuery['finance']['contact']

interface ModalProps {
  contact: NonNullable<FinanceContact>
  onClose: () => void
  contactType: FinanceValueObjectsContactTypeEnum
}

const DeleteContactModal = ({ contact, onClose, contactType }: ModalProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const [deleteContact, { loading }] = useMutation(DeleteFinanceContactDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceContact,
  })

  const onDelete = async () => {
    const result = await deleteContact({ variables: { id: contact.id, companyId: company.id } })

    const handled = handleResult(result, handleResultWithFlash(onClose))

    if (handled.success) {
      const destinationUrl =
        contactType === 'client'
          ? navigation.finance.sales.clients.fullPath()
          : navigation.finance.spending.vendors.fullPath()

      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.contacts.delete.success'),
      })

      navigate(destinationUrl)
    }
  }

  return (
    <Modal
      title={i18n.t('finance.contacts.delete.title')}
      description={i18n.t('finance.contacts.delete.description')}
      illustration="alert"
      onSubmit={() => onDelete()}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={loading}>
            {i18n.t('finance.contacts.delete.button')}
          </Button>
        </Box>
      }
    />
  )
}

interface Props {
  contact?: FinanceContact
  canDelete: boolean
  contactType: FinanceValueObjectsContactTypeEnum
}

export const ContactHeader = ({ contact, canDelete, contactType }: Props) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  if (!contact) {
    return
  }

  return (
    <>
      <ResourceHeader
        title={contactFullName(contact)}
        metadata={[
          contact.taxId
            ? {
                label: 'ID',
                value: {
                  type: 'text',
                  content: contact.taxId,
                },
              }
            : undefined,
        ]}
        otherActions={
          canDelete
            ? [
                {
                  label: i18n.t('finance.contacts.actions.delete'),
                  icon: Delete,
                  critical: true,
                  onClick: () => {
                    setShowDeleteModal(true)
                  },
                },
              ]
            : undefined
        }
      />
      {showDeleteModal && (
        <DeleteContactModal
          contact={contact}
          contactType={contactType}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </>
  )
}
